"use client";

import { useMemo } from "react";
import Link from "next/link";
import { useParams } from "next/navigation";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Icon from "@/components/ui/AppIcon";
import { blogArticles, categoryColors } from "../data/articles";
import CommentsSection from "./CommentsSection";

export default function ArticleDetailPage() {
  const params = useParams();
  const slug = params?.slug as string;

  const article = useMemo(
    () => blogArticles.find((a) => a.slug === slug),
    [slug]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return blogArticles
      .filter(
        (a) =>
          a.id !== article.id &&
          (a.category === article.category ||
            a.tags.some((t) => article.tags.includes(t)))
      )
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-[#0C1B33] flex flex-col">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center gap-6 px-6">
          <Icon name="DocumentTextIcon" size={56} className="text-cream/20" />
          <h1 className="font-display text-2xl text-cream">Article Not Found</h1>
          <p className="text-cream/50 font-body text-center max-w-md">
            The article you are looking for does not exist or may have been moved.
          </p>
          <Link
            href="/blog"
            className="flex items-center gap-2 text-gold hover:text-gold/80 font-display text-sm uppercase tracking-wider transition-colors"
          >
            <Icon name="ArrowLeftIcon" size={14} className="" />
            Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const paragraphs = article.content.split("\n\n");

  return (
    <div className="min-h-screen bg-[#0C1B33]">
      <Header />

      {/* Article Hero */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628] to-[#0C1B33]" />
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle at 30% 50%, #C9A84C 0%, transparent 50%)" }} />
        <div className="relative z-10 max-w-4xl mx-auto px-6">
          {/* Back button */}
          <Link
            href="/blog"
            className="inline-flex items-center gap-2 text-cream/50 hover:text-gold font-display text-xs uppercase tracking-wider transition-colors mb-8"
          >
            <Icon name="ArrowLeftIcon" size={14} className="" />
            Back to Blog
          </Link>

          {/* Category */}
          <div className="mb-5">
            <span
              className={`text-[10px] font-display font-600 uppercase tracking-wider px-3 py-1.5 rounded-full border ${
                categoryColors[article.category] ??
                "bg-[rgba(201,168,76,0.15)] text-gold border-[rgba(201,168,76,0.3)]"
              }`}
            >
              {article.category}
            </span>
          </div>

          {/* Title */}
          <h1 className="font-display text-3xl md:text-4xl font-700 text-cream leading-tight mb-6">
            {article.title}
          </h1>

          {/* Author + Meta row */}
          <div className="flex flex-wrap items-center gap-6 pb-8 border-b border-[rgba(201,168,76,0.12)]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gold/15 border border-gold/20 flex items-center justify-center">
                <Icon name="UserIcon" size={18} className="text-gold/60" />
              </div>
              <div>
                <p className="font-display text-sm text-cream">{article.author}</p>
                <p className="text-[11px] font-body text-cream/40">{article.authorRole}</p>
              </div>
            </div>
            <div className="flex items-center gap-5 text-[12px] font-body text-cream/40">
              <span className="flex items-center gap-1.5">
                <Icon name="CalendarIcon" size={13} className="text-gold/40" />
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <Icon name="ClockIcon" size={13} className="text-gold/40" />
                {article.readTime}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Thumbnail placeholder */}
      <div className="max-w-4xl mx-auto px-6 mb-10">
        <div className="relative h-64 md:h-80 rounded-xl bg-gradient-to-br from-[#0a1628] to-[#112244] border border-[rgba(201,168,76,0.12)] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 50% 50%, #C9A84C 0%, transparent 60%)" }} />
          <Icon name="DocumentTextIcon" size={64} className="text-gold/15" />
        </div>
      </div>

      {/* Article Body */}
      <article className="max-w-4xl mx-auto px-6 pb-16">
        <div className="prose-content space-y-5">
          {paragraphs.map((para, i) => {
            if (para.startsWith("## ")) {
              return (
                <h2
                  key={i}
                  className="font-display text-xl font-700 text-cream mt-10 mb-4 pb-2 border-b border-[rgba(201,168,76,0.12)]"
                >
                  {para.replace("## ", "")}
                </h2>
              );
            }
            if (para.startsWith("### ")) {
              return (
                <h3
                  key={i}
                  className="font-display text-lg font-600 text-gold mt-8 mb-3"
                >
                  {para.replace("### ", "")}
                </h3>
              );
            }
            if (para.startsWith("- ") || para.includes("\n- ")) {
              const items = para.split("\n").filter((l) => l.startsWith("- "));
              return (
                <ul key={i} className="space-y-2 pl-4">
                  {items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-[14px] font-body text-cream/70 leading-relaxed">
                      <span className="mt-2 w-1 h-1 rounded-full bg-gold/60 flex-shrink-0" />
                      <span dangerouslySetInnerHTML={{ __html: item.replace("- ", "").replace(/\*\*(.*?)\*\*/g, '<strong class="text-cream font-600">$1</strong>') }} />
                    </li>
                  ))}
                </ul>
              );
            }
            if (/^\d+\./.test(para) || para.includes("\n1. ")) {
              const items = para.split("\n").filter((l) => /^\d+\./.test(l));
              return (
                <ol key={i} className="space-y-2 pl-4">
                  {items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-[14px] font-body text-cream/70 leading-relaxed">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gold/15 border border-gold/20 flex items-center justify-center text-[10px] font-display text-gold">{j + 1}</span>
                      <span dangerouslySetInnerHTML={{ __html: item.replace(/^\d+\.\s*/, "").replace(/\*\*(.*?)\*\*/g, '<strong class="text-cream font-600">$1</strong>') }} />
                    </li>
                  ))}
                </ol>
              );
            }
            if (para.trim() === "") return null;
            return (
              <p
                key={i}
                className="text-[15px] font-body text-cream/70 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: para.replace(/\*\*(.*?)\*\*/g, '<strong class="text-cream font-600">$1</strong>') }}
              />
            );
          })}
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-[rgba(201,168,76,0.12)]">
          {article.tags.map((tag) => (
            <span
              key={tag}
              className="text-[10px] font-display uppercase tracking-wider px-3 py-1 rounded-full bg-[rgba(255,255,255,0.04)] border border-[rgba(201,168,76,0.15)] text-cream/50"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Author card */}
        <div className="mt-10 p-6 rounded-xl border border-[rgba(201,168,76,0.15)] bg-[rgba(201,168,76,0.03)] flex items-start gap-5">
          <div className="w-14 h-14 rounded-full bg-gold/15 border border-gold/20 flex items-center justify-center flex-shrink-0">
            <Icon name="UserIcon" size={24} className="text-gold/60" />
          </div>
          <div>
            <p className="font-display font-600 text-cream mb-0.5">{article.author}</p>
            <p className="text-[12px] font-body text-gold/70 mb-3">{article.authorRole} — StanDave LLC</p>
            <p className="text-[13px] font-body text-cream/50 leading-relaxed">
              A member of the StanDave LLC expert team, bringing deep industry knowledge and strategic insight to help clients navigate complex global business challenges.
            </p>
          </div>
        </div>
      </article>

      {/* Comments Section */}
      <CommentsSection />

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="border-t border-[rgba(201,168,76,0.12)] bg-[rgba(255,255,255,0.01)] py-14">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1 h-6 bg-gold rounded-full" />
              <h2 className="font-display text-lg font-600 text-cream">Related Articles</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map((rel) => (
                <Link key={rel.id} href={`/blog/${rel.slug}`} className="group">
                  <div className="rounded-xl border border-[rgba(201,168,76,0.12)] bg-[rgba(255,255,255,0.02)] hover:bg-[rgba(201,168,76,0.04)] hover:border-[rgba(201,168,76,0.28)] transition-all duration-300 p-5">
                    <span
                      className={`text-[10px] font-display font-600 uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                        categoryColors[rel.category] ??
                        "bg-[rgba(201,168,76,0.15)] text-gold border-[rgba(201,168,76,0.3)]"
                      }`}
                    >
                      {rel.category}
                    </span>
                    <h3 className="font-display font-600 text-[14px] text-cream group-hover:text-gold transition-colors mt-3 mb-2 line-clamp-2">
                      {rel.title}
                    </h3>
                    <div className="flex items-center gap-3 text-[11px] font-body text-cream/35">
                      <span>{rel.author}</span>
                      <span>·</span>
                      <span>{rel.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Back to blog CTA */}
      <div className="max-w-7xl mx-auto px-6 py-10 flex justify-center">
        <Link
          href="/blog"
          className="inline-flex items-center gap-2 px-8 py-3 border border-[rgba(201,168,76,0.3)] text-gold hover:bg-gold/10 font-display text-sm uppercase tracking-wider rounded-sm transition-all duration-200"
        >
          <Icon name="ArrowLeftIcon" size={14} className="" />
          Back to All Articles
        </Link>
      </div>

      <Footer />
    </div>
  );
}
